import React, { useState, useEffect, useCallback } from 'react';
import { fetchDashboardData, fetchRegionDetail } from './dataService';
import Header from './components/Header';
import KpiCards from './components/KpiCards';
import MapSection from './components/MapSection';
import CompetitionCharts from './components/CompetitionCharts';
import PeopleSection from './components/PeopleSection';
import PathwayFunnel from './components/PathwayFunnel';
import RegionalTable from './components/RegionalTable';
import SessionsSection from './components/SessionsSection';
import MatchesSection from './components/MatchesSection';
import './App.css';

function App() {
  const [data, setData] = useState(null);
  const [regionDetail, setRegionDetail] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [compSubTab, setCompSubTab] = useState('analysis'); // 'analysis' | 'matches'

  useEffect(() => {
    fetchDashboardData()
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  const selectRegion = useCallback((name) => {
    if (!name || name === selectedRegion) {
      setSelectedRegion(null);
      setRegionDetail(null);
      return;
    }
    setSelectedRegion(name);
    if (data) {
      fetchRegionDetail(name, data).then(setRegionDetail);
    }
  }, [selectedRegion, data]);

  if (loading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="spinner" />
        <p style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Loading ICC Census Data...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="loading-screen" data-testid="error-screen">
        <p style={{ color: 'var(--brand-accent)' }}>Failed to load dashboard data</p>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'involvement', label: 'Involvement' },
    { id: 'competitions', label: 'Competitions' },
    { id: 'people', label: 'Coaches & Umpires' },
  ];

  return (
    <div data-testid="dashboard-app">
      <Header summary={data.summary} />
      <main className="main-container">
        <KpiCards data={data} />

        <div className="tab-nav" data-testid="tab-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            <MapSection
              regions={data.regions}
              sessionsByRegion={data.sessions_by_region}
              selectedRegion={selectedRegion}
              regionDetail={regionDetail}
              onSelectRegion={selectRegion}
            />
            <PathwayFunnel data={data} />
            <RegionalTable regions={data.regions} onSelectRegion={selectRegion} selectedRegion={selectedRegion} />
          </>
        )}

        {activeTab === 'involvement' && <SessionsSection data={data} />}

        {activeTab === 'competitions' && (
          <>
            {/* Competitions sub-tab bar */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
              {[
                { id: 'analysis', label: 'Analysis' },
                { id: 'matches',  label: 'Matches, Grounds & Officials' },
              ].map(st => (
                <button
                  key={st.id}
                  onClick={() => setCompSubTab(st.id)}
                  style={{
                    padding: '0.4rem 1.1rem',
                    borderRadius: 6,
                    border: `1px solid ${compSubTab === st.id ? 'var(--brand-primary)' : 'rgba(255,255,255,0.15)'}`,
                    background: compSubTab === st.id ? 'var(--brand-primary)' : 'rgba(255,255,255,0.04)',
                    color: compSubTab === st.id ? '#fff' : 'var(--text-secondary)',
                    fontWeight: compSubTab === st.id ? 700 : 500,
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {st.label}
                </button>
              ))}
            </div>
            {compSubTab === 'analysis' && <CompetitionCharts data={data} />}
            {compSubTab === 'matches'  && <MatchesSection data={data} />}
          </>
        )}

        {activeTab === 'people' && <PeopleSection data={data} />}
      </main>

      <footer className="footer" data-testid="footer">
        <p>Cricket Namibia &middot; ICC Census 2025</p>
        <p style={{ marginTop: '0.25rem' }}>Data source: Final ICC 2025 Submission Files</p>
      </footer>
    </div>
  );
}

export default App;
